import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as BarTooltip,
  Label,
} from 'recharts';
import styles, { rechartsStyles } from './MeterDetailsQualityChart.styles';
import { labelFormatterFromPayload } from '../../utils/meterDetails.helpers';
import { makeDefaultDateRange21 } from '../../utils/dates';
import { useMeterQuality } from '../../hooks/useMeterQuality';

import {
  buildQualitySeries,
  CHART_COLORS,
  LABEL_MAP,
} from '../../../dataQuality/utils/dataQualityBar.helpers';

import {
  computeEffectiveDateRange,
  parseDatesFromSeries,
  computeMonthYearLabel,
} from '../../utils/meterDetailsChart.helpers';

export function MeterDetailsQualityLegend({ styles: legendStyles }) {
  const entries = [
    { label: LABEL_MAP.Missing_Type7, color: CHART_COLORS[0] },
    { label: LABEL_MAP.Actual,        color: CHART_COLORS[1] },
    { label: LABEL_MAP.Missing_Type9, color: CHART_COLORS[2] },
    { label: LABEL_MAP.Uncertain,     color: CHART_COLORS[3] },
    { label: LABEL_MAP.PendingMissing,color: CHART_COLORS[4] },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', ...(legendStyles || {}) }}>
      {entries.map((e) => (
        <Box key={e.label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 4, backgroundColor: e.color }} />
          <Typography variant="caption" sx={{ fontSize: 11, opacity: 0.85 }}>
            {e.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function MeterDetailsQualityChart({
  meterNumber,
  startDate,
  endDate,
  data = [],
  loading = false,
  evn,
}) {
  const [localRange] = useState(() => makeDefaultDateRange21());

  const effectiveRange = useMemo(
    () => computeEffectiveDateRange(undefined, startDate, endDate, localRange),
    [startDate, endDate, localRange]
  );

  const { qualityData: fetchedData = [], loading: fetching } =
    useMeterQuality(meterNumber, effectiveRange, evn);

  const source = (Array.isArray(fetchedData) && fetchedData.length) ? fetchedData : data;

  const chartData = useMemo(() => buildQualitySeries(source, effectiveRange), [
    source,
    effectiveRange,
  ]);

  const parsedDates = useMemo(
    () => parseDatesFromSeries(chartData, (r) => r?.iso),
    [chartData]
  );

  const monthYearLabel = useMemo(
    () => computeMonthYearLabel(parsedDates, effectiveRange),
    [parsedDates, effectiveRange]
  );

  const manyPoints = chartData.length > 16;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerRow}>
        <Typography variant="subtitle2" sx={styles.headerTitle}>Daily Quality</Typography>
      </Box>

      <Box sx={styles.chartWrapper}>
        {(loading || fetching) && (
          <Box sx={styles.loadingOverlay}>
            <Typography sx={styles.loadingText}>Loading…</Typography>
          </Box>
        )}

        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={rechartsStyles.margin}
            barCategoryGap="18%"
            maxBarSize={72}
          >
            <XAxis
              dataKey="day"
              interval={0}
              minTickGap={4}
              tickMargin={10}
              height={manyPoints ? 28 : 22}
              tick={{ fontSize: 10, dy: 6 }}
            >
              <Label
                value={monthYearLabel}
                position="insideBottom"
                dy={24}
                style={{ textAnchor: 'middle', fontSize: 11, opacity: 0.75 }}
              />
            </XAxis>

            <YAxis
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 10 }}
              width={34}
            />

            <BarTooltip
              labelFormatter={labelFormatterFromPayload}
              formatter={(v, name, props) => {
                const display = props?.payload?.__display;
                const key = props?.dataKey || name;
                const intPct =
                  display && Number.isFinite(display[key])
                    ? display[key]
                    : Math.round(v);
                return [`${intPct}%`, LABEL_MAP[key] ?? key];
              }}
              wrapperStyle={rechartsStyles.tooltip}
            />

            <Bar dataKey="Missing_Type7" stackId="a" fill={CHART_COLORS[0]} />
            <Bar dataKey="Actual"        stackId="a" fill={CHART_COLORS[1]} />
            <Bar dataKey="Missing_Type9" stackId="a" fill={CHART_COLORS[2]} />
            <Bar dataKey="Uncertain"     stackId="a" fill={CHART_COLORS[3]} />
            <Bar dataKey="PendingMissing"stackId="a" fill={CHART_COLORS[4]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}